import type { ComponentProps } from "react";
import { FormattedMessage } from "react-intl";
import { useMatch } from "react-router-dom";

import { Feature } from "graphql_globals";
import { EVENT } from "constants/analytics";
import { segmentTrack } from "util/segment";
import {
  SETTINGS_PATH,
  EASYLINK_DASHBOARD_PATH,
  TEMPLATES_DASHBOARD_PATH,
  TRANSACTION_PATH,
} from "util/routes";
import { PATH as ON_DEMAND_QUEUE_PATH } from "common/notary/on_demand_queue/path";
import {
  isNotaryODN,
  isNotaryNST,
  isNotaryNSTCallReady,
  isNotaryODNCallReady,
  isNotaryIHNCallReady,
} from "common/notary/capacity";
import { useUpcomingPath } from "common/closing_agenda";
import type { ProofPortal } from "constants/app_subdomains";
import { SCHEDULED_PATH, CLOSING_AGENDA_PATH } from "common/closing_agenda/path";
import { usePermissions } from "common/core/current_user_role";
import type { ProofFrame_viewer_user as User } from "common/proof_frame/index.query.graphql";
import {
  TEAM_PATH,
  GET_STARTED_PATH,
  COMPANY_DIRECTORY_PATH,
  USER_MANAGEMENT_PATH,
  useIsCommandCenter,
} from "common/proof_frame/path";
import { NavItem } from "common/proof_frame/org_side_nav/nav_item";
import { useHideGetStarted } from "common/get_started/common/util";

type SideNavLinksProps = {
  portal: ProofPortal;
  sideNavCollapsed: boolean;
  user: User;
};
type NavItemProps = ComponentProps<typeof NavItem>;

const myClosingsLabel = (
  <FormattedMessage id="0f64cca9-4be7-4066-a200-38b93d27dae3" defaultMessage="My closings" />
);
const sendAndManageLabel = (
  <FormattedMessage id="30466052-9694-4f8e-8c6a-c49989e2e3ec" defaultMessage="Send & manage" />
);
const onDemandLabel = (
  <FormattedMessage id="65cdc54f-3c49-41ee-8c1f-973604791384" defaultMessage="On demand" />
);
const meetingsLabel = (
  <FormattedMessage id="a1599230-f749-4d45-9118-020ca4023927" defaultMessage="Meetings" />
);
const orgSettingsLabel = (
  <FormattedMessage id="10348fce-f7ca-47dd-ac6f-2bba4435cbe5" defaultMessage="Settings" />
);
const usersLabel = (
  <FormattedMessage id="174a66b9-2000-49a9-9f83-13b1fc44bc5c" defaultMessage="Users" />
);
const getStartedLabel = (
  <FormattedMessage id="91bb612d-2dec-4814-8e11-f3872bc44dd9" defaultMessage="Get started" />
);
const companyDirectoryLabel = (
  <FormattedMessage id="8acdec1b-2cdf-425a-a207-b13ace8bdcde" defaultMessage="Company directory" />
);
const userManagementLabel = (
  <FormattedMessage id="f25efb14-b846-4afe-b676-0497709315cf" defaultMessage="User management" />
);

function useIsPathActive(path: string): boolean {
  return Boolean(useMatch({ path, end: false }));
}

export function SideNavLinks({ portal, sideNavCollapsed, user }: SideNavLinksProps) {
  const { organization, notaryProfile } = user;
  const isBusinessPortal = portal === "business";
  const isTitlePortal = portal === "title";
  const isLenderPortal = portal === "lender";
  const isIHNOrganization = organization?.featureList.some(
    (feat) => feat === Feature.ORGANIZATION_NOTARIES,
  );
  const { hasPermissionFor } = usePermissions();
  const canViewTeamDetails = hasPermissionFor("viewTeamDetails");
  const canViewOrganizationDetails = hasPermissionFor("viewOrganizationDetails");
  const canViewOrganizationTransactions = hasPermissionFor("viewOrganizationTransactions");
  const canViewMeetings = hasPermissionFor("viewMeetings");

  const isEasylinkActive = useIsPathActive(EASYLINK_DASHBOARD_PATH);
  const isTemplatesActive = useIsPathActive(TEMPLATES_DASHBOARD_PATH);
  const hideGetStarted = useHideGetStarted();
  const isCommandCenter = useIsCommandCenter();

  const sendAndManage: NavItemProps = {
    handleUrl: TRANSACTION_PATH,
    iconName: "dashboard",
    label: sendAndManageLabel,
    isActive: useIsPathActive("/transaction") || isEasylinkActive || isTemplatesActive,
  };
  const myClosings: NavItemProps = {
    handleUrl: TRANSACTION_PATH,
    iconName: "dashboard",
    label: myClosingsLabel,
    isActive: useIsPathActive(TRANSACTION_PATH),
  };
  const onDemand: NavItemProps = {
    handleUrl: ON_DEMAND_QUEUE_PATH,
    iconName: "phone",
    label: onDemandLabel,
    isActive: useIsPathActive(ON_DEMAND_QUEUE_PATH),
    queueCountKey: isNotaryODNCallReady(notaryProfile) && "waitingNODCalls",
  };
  const meetings: NavItemProps = {
    handleUrl: useUpcomingPath(isBusinessPortal ? SCHEDULED_PATH : CLOSING_AGENDA_PATH),
    iconName: "calendar",
    label: meetingsLabel,
    isActive: useIsPathActive("/meetings"),
    queueCountKey:
      (isNotaryNSTCallReady(notaryProfile) || isNotaryIHNCallReady(notaryProfile)) &&
      "waitingClientCalls",
  };
  const organizationSettings: NavItemProps = {
    handleUrl: SETTINGS_PATH,
    iconName: "settings",
    label: orgSettingsLabel,
    isActive: useIsPathActive(SETTINGS_PATH),
  };
  const teamMembers: NavItemProps = {
    handleUrl: TEAM_PATH,
    iconName: "employees",
    label: usersLabel,
    isActive: useIsPathActive(TEAM_PATH),
  };
  const getStarted: NavItemProps = {
    handleUrl: GET_STARTED_PATH,
    iconName: "get-started",
    label: getStartedLabel,
    onClick: () => segmentTrack(EVENT.CLICKED_GET_STARTED),
    isActive: useIsPathActive(GET_STARTED_PATH),
  };
  const companyDirectory: NavItemProps = {
    handleUrl: COMPANY_DIRECTORY_PATH,
    iconName: "directory",
    label: companyDirectoryLabel,
    isActive: useIsPathActive(COMPANY_DIRECTORY_PATH),
  };
  const userManagement: NavItemProps = {
    handleUrl: USER_MANAGEMENT_PATH,
    iconName: "business",
    label: userManagementLabel,
    isActive: useIsPathActive(USER_MANAGEMENT_PATH),
  };

  function getNavItems(): NavItemProps[] {
    if (isBusinessPortal && isNotaryODN(notaryProfile)) {
      return [getStarted, sendAndManage, onDemand, meetings, organizationSettings];
    } else if (isBusinessPortal && isNotaryNST(notaryProfile)) {
      return [getStarted, sendAndManage, meetings, organizationSettings];
    } else if (isBusinessPortal && isIHNOrganization) {
      return [sendAndManage, meetings, organizationSettings, teamMembers, getStarted];
    } else if (isBusinessPortal) {
      return [sendAndManage, organizationSettings, teamMembers, getStarted];
    }

    if (isTitlePortal || isLenderPortal) {
      const navItems = [myClosings, organizationSettings, teamMembers];
      if (isIHNOrganization) {
        navItems.splice(1, 0, meetings);
      }
      if (isTitlePortal) {
        navItems.push(getStarted);
      }
      return navItems;
    }

    if (isCommandCenter) {
      return [companyDirectory, userManagement];
    }

    return [];
  }

  function filterByPermissionsAndFeatures(items: NavItemProps[]) {
    return items.filter((item) => {
      switch (item) {
        case getStarted:
          return (
            !hideGetStarted &&
            (canViewTeamDetails || canViewOrganizationDetails || canViewOrganizationTransactions)
          );
        case meetings:
          return canViewMeetings;
        case sendAndManage:
          return canViewOrganizationTransactions;
        case organizationSettings:
          return canViewOrganizationDetails;
        case teamMembers:
          return canViewTeamDetails;
        case myClosings:
          return canViewOrganizationTransactions;
        default:
          return true;
      }
    });
  }

  return (
    <ul>
      {filterByPermissionsAndFeatures(getNavItems()).map((navItemProps) => (
        <NavItem key={navItemProps.handleUrl} isCollapsed={sideNavCollapsed} {...navItemProps} />
      ))}
    </ul>
  );
}
